/**
 * My profile view - declarative React component.
 *
 * Elm-ish architecture: This component is THIN.
 * - No business logic
 * - No state mutations
 * - No API calls
 * - Only renders based on model state
 * - Delegates all actions to callbacks from the program
 *
 * S018 authority: Figma node 39:4695
 *
 * C7: Correct action dispositions.
 * | Control               | Disposition                                           |
 * | --------------------- | ----------------------------------------------------- |
 * | Edit avatar           | Disabled/unavailable; profile-edit successor          |
 * | Редактировать профиль | Disabled/unavailable; profile-edit successor          |
 * | Настройки поиска      | Disabled/unavailable; search-settings successor       |
 * | Уведомления           | Disabled/unavailable; notification-settings successor |
 * | Конфиденциальность    | Link to `/privacy`                                    |
 * | Поддержка             | Link to `/contact`                                    |
 * | Logout                | Production action                                     |
 */
import { Link } from 'react-router-dom';
import { RoutePaths } from '@/routes';
import type { MyProfileModel } from './model';
import './MyProfile.css';

/**
 * Props for the MyProfileView component.
 */
export interface MyProfileViewProps {
  /** Current model state */
  model: MyProfileModel;
  /** Retry loading the profile */
  onRetry: () => void;
  /** Request logout */
  onLogout: () => void;
  /** Clear logout error */
  onClearLogoutError: () => void;
}

/**
 * Loading state with skeleton.
 */
function LoadingState() {
  return (
    <div className="my-profile-loading" data-testid="my-profile-loading">
      <div className="loading-skeleton avatar-skeleton" aria-label="Загрузка профиля" />
      <div className="loading-skeleton text-skeleton text-skeleton-name" />
      <div className="loading-skeleton text-skeleton text-skeleton-city" />
    </div>
  );
}

/**
 * Load failure state with retry.
 */
function LoadFailureState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="my-profile-error" data-testid="my-profile-error">
      <p className="error-message">Не удалось загрузить профиль</p>
      <button
        className="retry-button"
        onClick={onRetry}
        data-testid="retry-button"
      >
        Попробовать снова
      </button>
    </div>
  );
}

/**
 * Logout failure alert.
 * C4: Uses LogoutErrorCleared message to dismiss.
 */
function LogoutErrorAlert({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div className="logout-error-alert" role="alert" data-testid="logout-error">
      <span>Не удалось выйти из аккаунта</span>
      <button
        className="alert-dismiss"
        onClick={onDismiss}
        data-testid="logout-error-dismiss"
      >
        Ок
      </button>
    </div>
  );
}

/**
 * Profile avatar with edit affordance.
 * C6: Uses Latin initial (not Cyrillic 'А') per Figma asset.
 * Currently unavailable - disabled per S018 disposition.
 */
function ProfileAvatar({ name }: { name: string }) {
  const initial = name.charAt(0).toUpperCase();
  return (
    <div className="profile-avatar-container" data-testid="profile-avatar">
      <div className="profile-avatar">
        <span className="avatar-initial" aria-hidden="true">{initial}</span>
      </div>
      {/* C7: Edit avatar unavailable - successor is profile-edit ACT */}
      <button
        className="edit-avatar-button"
        aria-label="Изменить аватар (недоступно)"
        data-testid="edit-avatar-button"
        disabled
        title="Функция редактирования аватара будет доступна в следующем обновлении"
      >
        <span className="edit-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="13" height="13">
            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
          </svg>
        </span>
      </button>
    </div>
  );
}

/**
 * Profile identity display (name, age, city).
 */
function ProfileIdentity({ name, age, city }: {
  name: string;
  age: number;
  city: string;
}) {
  return (
    <div className="profile-identity" data-testid="profile-identity">
      <h2 className="profile-name" data-testid="profile-name">{name}, {age}</h2>
      <p className="profile-city" data-testid="profile-city">{city}</p>
    </div>
  );
}

/**
 * Profile completion card with progress bar.
 * Figma-bound: 65% completion, teal progress fill.
 * Shows fallback when completionPercent is undefined (backend contract PARTIAL).
 */
function ProfileCompletionCard({ percent }: { percent: number | undefined }) {
  // PARTIAL: When completionPercent is undefined, show placeholder
  // This is a known gap per ACT-INDEEP-FRONTEND-MY-PROFILE-API-ADAPTER01
  if (percent === undefined) {
    return (
      <div className="profile-completion-card" data-testid="profile-completion-card">
        <div className="completion-header">
          <span className="completion-label">Заполненность профиля</span>
          <span className="completion-percent">—</span>
        </div>
        <p className="completion-hint">
          Заполните профиль, чтобы получать больше предложений
        </p>
      </div>
    );
  }

  const fillWidth = `${percent}%`;
  return (
    <div className="profile-completion-card" data-testid="profile-completion-card">
      <div className="completion-header">
        <span className="completion-label">Заполненность профиля</span>
        <span className="completion-percent">{percent}%</span>
      </div>
      <div
        className="progress-track"
        role="progressbar"
        aria-label="Заполнение профиля"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-valuetext={`Профиль заполнен на ${percent} процентов`}
        data-testid="progress-bar"
      >
        <div className="progress-fill" style={{ width: fillWidth }} />
      </div>
      <p className="completion-hint">
        Заполните профиль, чтобы получать больше предложений
      </p>
    </div>
  );
}

/**
 * Unavailable action row.
 * C7: Visually indicates unavailability, no navigation.
 * No chevron, no click events, not a link.
 */
function UnavailableActionRow({
  label,
  testId,
}: {
  label: string;
  testId: string;
}) {
  return (
    <div
      className="action-row action-row-unavailable"
      aria-disabled="true"
      data-testid={testId}
      title="Будет доступно в следующем обновлении"
    >
      <span className="action-label">{label}</span>
      <span className="action-unavailable-badge">Скоро</span>
    </div>
  );
}

/**
 * Logout button.
 */
function LogoutButton({ isSubmitting, onClick }: {
  isSubmitting: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className="logout-button"
      onClick={onClick}
      disabled={isSubmitting}
      data-testid="logout-button"
    >
      {isSubmitting ? 'Выход...' : 'Выйти из аккаунта'}
    </button>
  );
}

/**
 * Ready profile view.
 */
function ReadyProfileView({
  model,
  onLogout,
  onClearLogoutError,
}: {
  model: Extract<MyProfileModel, { status: 'ready' }>;
  onLogout: () => void;
  onClearLogoutError: () => void;
}) {
  const { profile, logout } = model;
  const isLogoutSubmitting = logout.status === 'submitting';
  const hasLogoutError = logout.status === 'failed';

  return (
    <div className="my-profile-content" data-testid="my-profile-content">
      {/* Avatar and identity section */}
      <div className="profile-header-section">
        <ProfileAvatar name={profile.displayName} />
        <ProfileIdentity
          name={profile.displayName}
          age={profile.age}
          city={profile.city}
        />
      </div>

      {/* Completion card */}
      <ProfileCompletionCard percent={profile.completionPercent} />

      {/* Action rows */}
      <nav className="profile-actions" aria-label="Профиль">
        {/* C7: Редактировать профиль — now wired to the shared
           profile-questionnaire route in EDIT mode (Bucket B). */}
        <Link
          to={`${RoutePaths.PROFILE_QUESTIONNAIRE}?mode=edit`}
          className="action-row"
          data-testid="action-edit-profile"
        >
          <span className="action-label">Редактировать профиль</span>
          <span className="action-chevron" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" fill="currentColor"/>
            </svg>
          </span>
        </Link>

        {/* C7: Настройки поиска - unavailable, successor is search-settings ACT */}
        <UnavailableActionRow
          label="Настройки поиска"
          testId="action-search-settings"
        />

        {/* C7: Уведомления - unavailable, successor is notification-settings ACT */}
        <UnavailableActionRow
          label="Уведомления"
          testId="action-notifications"
        />

        {/* Конфиденциальность - links to privacy page */}
        <Link
          to={RoutePaths.PRIVACY}
          className="action-row"
          data-testid="action-privacy"
        >
          <span className="action-label">Конфиденциальность</span>
          <span className="action-chevron" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" fill="currentColor"/>
            </svg>
          </span>
        </Link>

        {/* Поддержка - links to contact page */}
        <Link
          to={RoutePaths.CONTACT}
          className="action-row"
          data-testid="action-support"
        >
          <span className="action-label">Поддержка</span>
          <span className="action-chevron" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" fill="currentColor"/>
            </svg>
          </span>
        </Link>
      </nav>

      {/* Logout error alert */}
      {hasLogoutError && (
        <LogoutErrorAlert onDismiss={onClearLogoutError} />
      )}

      {/* Logout button - C7: Production action */}
      <div className="logout-section">
        <LogoutButton
          isSubmitting={isLogoutSubmitting}
          onClick={onLogout}
        />
      </div>
    </div>
  );
}

/**
 * Main MyProfileView component.
 * Routes to appropriate sub-component based on model status.
 */
export function MyProfileView(props: MyProfileViewProps) {
  const { model, onRetry, onLogout, onClearLogoutError } = props;

  if (model.status === 'loading') {
    return <LoadingState />;
  }

  if (model.status === 'load_failed') {
    return <LoadFailureState onRetry={onRetry} />;
  }

  if (model.status === 'ready') {
    return (
      <ReadyProfileView
        model={model}
        onLogout={onLogout}
        onClearLogoutError={onClearLogoutError}
      />
    );
  }

  // Fallback - should not reach here
  return <LoadingState />;
}
